import type { Call } from '@entities/call';
import type { BaseUsecase } from '@usecases/base-usecase';
import type { UsecaseErrors } from '@usecases/usecase-errors';
import type { SearchRecordResult } from '@main/sf-connector/scv-object-manager';
import { ScvObjectManager } from '@main/sf-connector/scv-object-manager';
import { RepositoryFactory } from '@main/factories/repository.factory';
import { UserPresenceStatusStore } from '@main/sf-connector/user-presence-status-store';
import { FeatureFlagStore } from '@main/sf-connector/feature-flag-store';
import sleep from '@main/utils/sleep';
import { Logger } from '@main/utils/logger';

const MAX_VOICE_CALL_ATTEMPTS = 7;
const VOICE_CALL_RETRY_DELAY_MS = 1000;

const AVAILABILITY_POLL_MAX_ATTEMPTS = 12;
const AVAILABILITY_POLL_INTERVAL_MS = 5000;

export type RingIncomingCallRequest = Omit<Call, 'crmMetadata' | 'isMuted'>;
export type RingIncomingCallErrors = Partial<
  UsecaseErrors<RingIncomingCallRequest>
>;

export class InvalidRingIncomingCallRequest extends Error {
  override name = 'InvalidRingIncomingCallRequest';
  errors: RingIncomingCallErrors;

  constructor(message: string, errors: RingIncomingCallErrors) {
    super(message);
    this.errors = errors;
  }
}

export interface RingIncomingCallValidator {
  validate(
    call: RingIncomingCallRequest,
  ): Promise<RingIncomingCallErrors | void>;
}

export interface RingIncomingCallRepository {
  insertIncomingCall(call: RingIncomingCallRequest): Promise<void>;
}

export interface RingIncomingCallEventPublisher {
  publish(call: RingIncomingCallRequest): Promise<void>;
}

export interface RingIncomingCallUsecase
  extends BaseUsecase<RingIncomingCallRequest, void> {
  run(call: RingIncomingCallRequest): Promise<void>;
}

export class RingIncomingCall implements RingIncomingCallUsecase {
  protected logger: Logger;

  constructor(
    private validator: RingIncomingCallValidator,
    private repository: RingIncomingCallRepository,
    private publisher: RingIncomingCallEventPublisher,
  ) {
    this.logger = new Logger();
  }

  async run(call: RingIncomingCallRequest): Promise<void> {
    const [validationErrors] = await Promise.all([
      this.validator.validate(call),
      this.waitForVoiceCall(call.id),
    ]);

    await this.publisher.publish(call);

    if (validationErrors) {
      throw new InvalidRingIncomingCallRequest(
        'Invalid ring incoming call request',
        validationErrors,
      );
    }

    this.repository.insertIncomingCall(call);
  }

  private async waitForVoiceCall(callId: string): Promise<void> {
    for (let attempt = 1; attempt <= MAX_VOICE_CALL_ATTEMPTS; attempt++) {
      this.logger.info(`voice call search attempt ${attempt}`, { callId });
      try {
        const voiceCall = await ScvObjectManager.searchVoiceObject(callId, '');

        // SFI-1106: ring only when both the VoiceCall and its Aircall Voice
        // link are present, so the screen pop opens on a fully-linked record.
        // On the final attempt, ring even without the link (the integration
        // will recover via call.answered).
        if (voiceCall?.aircallscv2gp__Aircall_Voice__c) {
          this.logger.info('voice call found with aircall voice link', {
            callId,
            attempt,
          });
          return;
        }

        this.logger.info('voice call or aircall voice link not found', {
          callId,
          attempt,
        });
      } catch (e) {
        this.logger.error('voice call search error', {
          callId,
          attempt,
          error: e,
        });
      }

      if (attempt === MAX_VOICE_CALL_ATTEMPTS) {
        this.logger.warn(
          'aircall voice link not present after max attempts; ringing anyway',
          { callId },
        );
        return;
      }

      await sleep(VOICE_CALL_RETRY_DELAY_MS);
    }
  }
}

export interface RingIncomingCallCrmOps {
  preCall?: (req: RingIncomingCallRequest) => Promise<void>;
  postCall?: (req: RingIncomingCallRequest) => Promise<void>;
}

export class RingIncomingCallWithCrmOps extends RingIncomingCall {
  constructor(
    private crmOps: RingIncomingCallCrmOps,
    validator: RingIncomingCallValidator,
    repository: RingIncomingCallRepository,
    publisher: RingIncomingCallEventPublisher,
  ) {
    super(validator, repository, publisher);
  }

  override async run(call: RingIncomingCallRequest): Promise<void> {
    if (this.crmOps.preCall) {
      await this.crmOps.preCall(call);
    }

    this.logger.info('[RingIncomingCall] inbound call queuing polling', {
      enabled: FeatureFlagStore.isPollingEnabled,
    });
    if (FeatureFlagStore.isPollingEnabled) {
      const isAvailable = await this.pollAgentAvailability(call.from);
      if (!isAvailable) {
        return;
      }
    }

    await super.run(call);
    // Delay searchRecord logic by 5 seconds
    setTimeout(async () => {
      await this.searchRecord(call);
    }, 5000);

    if (this.crmOps.postCall) {
      await this.crmOps.postCall(call);
    }
  }

  private async pollAgentAvailability(fromNumber: string): Promise<boolean> {
    for (
      let attempt = 1;
      attempt <= AVAILABILITY_POLL_MAX_ATTEMPTS;
      attempt++
    ) {
      try {
        const result = await ScvObjectManager.checkAgentAvailability();
        this.logger.info('SCV Connector: Agent availability check', {
          attempt,
          availableCapacity: result.availableCapacity,
          message: result.message,
        });
        if (result.availableCapacity) {
          return true;
        }
      } catch (e) {
        this.logger.error('SCV Connector: Error checking agent availability', {
          attempt,
          error: e,
        });
        return true;
      }

      if (attempt < AVAILABILITY_POLL_MAX_ATTEMPTS) {
        await sleep(AVAILABILITY_POLL_INTERVAL_MS);
      }
    }

    ScvObjectManager.notifyScv({
      type: 'SCV.SHOW_TOAST',
      data: {
        title: 'Call Ended',
        message: `Call from ${fromNumber} is ended`,
        variant: 'warning',
      },
    });
    return false;
  }
  private async searchRecord(call: RingIncomingCallRequest): Promise<void> {
    const { from: from, id: id } = call;
    let popupNeeded = false;

    const userRepository = RepositoryFactory.buildMemUserRepository();
    const getCurrentStatusId =
      (await userRepository.getCurrentCRMStatus()) || '';

    const isStatusOnline =
      UserPresenceStatusStore.isStatusOnline(getCurrentStatusId);

    this.logger.info(
      'SCV Connector: Agent Availibility during Search Record:',
      {
        isStatusOnline,
      },
    );

    if (isStatusOnline) {
      popupNeeded = true;
    }

    const searchRecordResult: SearchRecordResult | undefined =
      await ScvObjectManager.searchRecord(from, popupNeeded, false);
    const recordToLink: SearchRecordResult | undefined = searchRecordResult;
    if (recordToLink || id.includes('_Rollback')) {
      setTimeout(async () => {
        this.linkRecordWithVoiceObject(id, recordToLink);
      }, 4000);
    }
    if (id.includes('_')) {
      const callIds = id.split('_');
      const callUuid = callIds[0];
      const [voiceObject] = await Promise.all([
        ScvObjectManager.searchVoiceObject(callUuid, ''),
      ]);
      if (voiceObject) {
        await ScvObjectManager.updateVoiceObject(
          id,
          callUuid,
          recordToLink ? recordToLink.Id : undefined,
          voiceObject.aircallscv2gp__Aircall_Voice__c,
        );
      }
    }
  }
  private async linkRecordWithVoiceObject(
    vendorCallKey: string,
    recordToLink?: SearchRecordResult,
  ): Promise<void> {
    vendorCallKey = vendorCallKey.includes('_Rollback')
      ? vendorCallKey.split('_Rollback')[0]
      : vendorCallKey;
    const [voiceObject] = await Promise.all([
      ScvObjectManager.searchVoiceObject(vendorCallKey, ''),
    ]);

    if (voiceObject) {
      if (voiceObject.aircallscv2gp__Aircall_Voice__c) {
        await ScvObjectManager.updateVoiceObject(
          vendorCallKey.includes('_Rollback')
            ? vendorCallKey
            : vendorCallKey + '_Rollback',
          vendorCallKey.includes('_Rollback')
            ? vendorCallKey
            : vendorCallKey + '_Rollback',
          recordToLink ? recordToLink.Id : undefined,
          voiceObject.aircallscv2gp__Aircall_Voice__c
            ? voiceObject.aircallscv2gp__Aircall_Voice__c
            : undefined,
        );
      } else {
        await ScvObjectManager.updateVoiceObject(
          vendorCallKey,
          vendorCallKey,
          recordToLink ? recordToLink.Id : undefined,
        );
      }
    }
  }
}
